import { FileClock, RefreshCw } from "lucide-react";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, EmptyState, Skeleton } from "../components/ui";
import { useAssetReceivables } from "../data/assets-platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";
import { useFlowReceivables } from "../data/flow-data";

export function ReceivablesPage() {
  const { tenant } = useTenantRuntime();
  const isFlow = tenant.product.commercialProductCode === "EVA_FLOW";
  const flowQuery = useFlowReceivables(isFlow);
  const query = useAssetReceivables();
  if (isFlow) {
    return (
      <Card
        title="Recebíveis"
        description="Parcelas e cobranças vinculadas a contratos de qualquer origem."
      >
        {flowQuery.isLoading && <Skeleton className="table-skeleton" />}
        {flowQuery.error && (
          <EmptyState title="Falha ao carregar recebíveis" description={flowQuery.error.message} />
        )}
        {!flowQuery.isLoading && !flowQuery.error && (flowQuery.data?.length ?? 0) === 0 && (
          <EmptyState
            title="Nenhum recebível cadastrado"
            description="Os valores a receber aparecerão aqui quando houver contratos e obrigações registrados."
          />
        )}
        {!flowQuery.isLoading && !flowQuery.error && (flowQuery.data?.length ?? 0) > 0 && (
          <DataTable
            rows={flowQuery.data ?? []}
            getKey={(row) => row.id}
            columns={[
              {
                key: "agreement",
                header: "Contrato",
                render: (row) => (
                  <span className="strong-link">
                    <FileClock size={15} /> {row.agreement}
                  </span>
                ),
              },
              { key: "description", header: "Descrição", render: (row) => row.description },
              { key: "sequence", header: "Parcela", render: (row) => row.sequence },
              { key: "due", header: "Vencimento", render: (row) => row.dueDate },
              { key: "original", header: "Original", render: (row) => row.original },
              { key: "paid", header: "Pago", render: (row) => row.paid },
              {
                key: "status",
                header: "Status",
                render: (row) => (
                  <Badge tone={row.status === "OVERDUE" ? "danger" : "info"}>{row.status}</Badge>
                ),
              },
            ]}
          />
        )}
      </Card>
    );
  }
  if (query.isLoading) return <Skeleton className="dashboard-skeleton" />;
  if (query.error) {
    return (
      <EmptyState
        title="Não foi possível carregar os recebíveis"
        description={query.error.message}
      />
    );
  }

  return (
    <div className="page-stack">
      <section className="metric-grid metric-grid-four">
        <article className="metric-card">
          <span>Saldo futuro</span>
          <strong>R$ 1,24 mi</strong>
          <small>Vendas e locações · demo</small>
        </article>
        <article className="metric-card">
          <span>Vencendo em 7 dias</span>
          <strong>R$ 48,6 mil</strong>
          <small>32 parcelas</small>
        </article>
        <article className="metric-card">
          <span>Vencido</span>
          <strong>R$ 86,4 mil</strong>
          <small>19 parcelas</small>
        </article>
        <article className="metric-card">
          <span>Atualizado hoje</span>
          <strong>R$ 88,1 mil</strong>
          <small>Multa e juros demonstrativos</small>
        </article>
      </section>

      <Card
        title="Recebíveis"
        description="Parcelas, aluguéis e valores atualizados para segunda via."
        action={
          <Button variant="secondary">
            <RefreshCw size={16} /> Atualizar carteira
          </Button>
        }
      >
        <DataTable
          rows={query.data ?? []}
          getKey={(row) => row.id}
          columns={[
            {
              key: "agreement",
              header: "Contrato",
              render: (row) => (
                <span className="strong-link">
                  <FileClock size={15} /> {row.agreement}
                </span>
              ),
            },
            { key: "customer", header: "Cliente", render: (row) => row.customer },
            { key: "asset", header: "Ativo", render: (row) => row.asset },
            { key: "sequence", header: "Parcela", render: (row) => row.sequence },
            { key: "due", header: "Vencimento", render: (row) => row.dueDate },
            { key: "original", header: "Original", render: (row) => row.original },
            { key: "updated", header: "Atualizado", render: (row) => row.updated },
            {
              key: "status",
              header: "Status",
              render: (row) => (
                <Badge tone={row.status === "OVERDUE" ? "danger" : "warning"}>{row.status}</Badge>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
